import { Component, OnInit, OnDestroy, ViewEncapsulation } from "@angular/core";
import { FormBuilder, Validators, FormGroup } from "@angular/forms";
import { Router } from "@angular/router";
import { Title } from "@angular/platform-browser";

import { Observable, Subscription } from "rxjs";
import { Store, select } from "@ngrx/store";

import {
  authenticate,
  recoverPassword,
} from "src/app/store/actions/login/login.actions";
import {
  AppState,
  loginStateIsLoading,
  loginStatePasswordRecovered,
  messagesStateGetMessage,
} from "src/app/store/reducers";
import { setMessageFlag } from "src/app/store/actions/messages/message.actions";

import { TranslateService } from "@ngx-translate/core";
import { SnackbarService } from "../../services/snackbar/snackbar.service";

import { environment } from "src/environments/environment";
import { AppSessionService } from "src/app/shared/services/app-session/app-session.service";

import { User } from "src/app/models/user";
import { AppMessage } from "../../../models/common/appMessage";
import { SnackbarType } from "src/app/models/snackbar/snackbar.model";
import { FormUtils } from "../../utils/formUtils";
import { PasswordRecoveryDTO } from "src/app/models/login/PasswordRecoveryDTO";
import { setAuthLanguage } from "../../utils/authenticationUtils";

const LOGIN_FORM_PARAMS = {
  id: "id",
  passwowrd: "password",
};

const PASSWORD_RECOVERY_FORM_PARAMS = {
  username: "username",
  email: "email",
};

@Component({
  selector: "app-login",
  templateUrl: "./login.component.html",
  styleUrls: ["./login.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class LoginComponent implements OnInit, OnDestroy {
  public loginForm: FormGroup;
  public passwordRecoveryForm: FormGroup;

  public showPasswordRecovery = false;
  public appVersion = environment?.appVersion;

  // Mesage Observable
  public message$: Observable<AppMessage>;
  public messageSubs: Subscription;

  // Loading Observalbe
  isLoading$: Observable<boolean>;

  // private version: String = version;
  appName = "";

  user: User;
  baseUrl: string;
  baseFallbackUrl: string;
  year: number;

  emailRegEx =
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

  // Password Recovered Observable and Subscription
  passwordRecovered$: Observable<boolean>;
  passwordRecoveredSub: Subscription;

  constructor(
    private fb: FormBuilder,
    public session: AppSessionService,
    private store: Store<AppState>,
    private titleService: Title,
    private translate: TranslateService,
    private snackbarService: SnackbarService,
    private router: Router
  ) {
    this.titleService.setTitle("Login");

    this.isLoading$ = this.store.pipe(select(loginStateIsLoading));
    this.message$ = this.store.pipe(select(messagesStateGetMessage));
    this.passwordRecovered$ = this.store.pipe(select(loginStatePasswordRecovered));
  }

  ngOnInit() {
    this.checkRedirect();
    setAuthLanguage(this.translate);
    this.enableMessages();
    this.setPasswordRecoveredSubscription();
    this.createLoginForm();
    this.setDefaultColors();
    this.user = new User();
    this.baseUrl = this.session.baseUrl;
    this.baseFallbackUrl = this.session.fallbackUrl;
    this.year = new Date().getFullYear();
  }
  
  setDefaultColors() {
    document.documentElement.style.setProperty("--primary", `#0e5b67`);
    document.documentElement.style.setProperty("--secondary", `#219174`);
  }

  enableMessages() {
    this.store.dispatch(setMessageFlag());
  }

  checkRedirect() {
    if (
      localStorage.getItem("isAuthenticated") &&
      localStorage.getItem("isAuthenticated") === "true"
    ) {
      this.router.navigate(["home"]);
    }
  }

  setPasswordRecoveredSubscription() {
    this.passwordRecoveredSub = this.passwordRecovered$.subscribe((passwordRecovered) => {
      if (passwordRecovered) {
        this.showPasswordRecovery = false;
      }
    });
  }

  createLoginForm() {
    this.loginForm = this.fb.group({
      [LOGIN_FORM_PARAMS.id]: [null, Validators.required],
      [LOGIN_FORM_PARAMS.passwowrd]: [null, Validators.required],
    });
  }

  tryLogin() {
    this.loginForm.markAllAsTouched();
    if (!this.loginForm.valid) {
      return;
    }

    this.user = this.loginForm.value;
    this.store.dispatch(authenticate({ user: this.user }));
  }

  showLoginForm() {
    this.showPasswordRecovery = false;
  }

  showPasswordRecoveryForm() {
    this.showPasswordRecovery = true;
    this.createPasswordRecoveryForm();
  }

  createPasswordRecoveryForm() {
    this.passwordRecoveryForm = this.fb.group({
      [PASSWORD_RECOVERY_FORM_PARAMS.username]: [
        null,
        FormUtils.noWhitespaceValidator,
      ],
      [PASSWORD_RECOVERY_FORM_PARAMS.email]: [
        null,
        Validators.pattern(this.emailRegEx),
      ],
    });
  }

  tryRecovery() {
    const usernameValue = this.passwordRecoveryForm.get(
      PASSWORD_RECOVERY_FORM_PARAMS.username
    ).value;
    const emailValue = this.passwordRecoveryForm.get(
      PASSWORD_RECOVERY_FORM_PARAMS.email
    ).value;
    if (!usernameValue && !emailValue) {
      this.snackbarService.show({
        message: this.translate.instant("Auth.AllFieldsEmpty"),
        type: SnackbarType.WARNING,
      });
      return;
    }

    if (!this.passwordRecoveryForm.valid) {
      this.snackbarService.show({
        message: this.translate.instant("Auth.PasswordRecoveryFormInvalid"),
        type: SnackbarType.ERROR,
      });
      return;
    }

    const passwordRecovery: PasswordRecoveryDTO = {
      username: usernameValue && usernameValue.length ? usernameValue : null,
      email: emailValue && emailValue.length ? emailValue : null,
      baseUrl: location.origin,
    };

    this.store.dispatch(recoverPassword({ passwordRecovery }));
  }

  ngOnDestroy(): void {
    if (this.messageSubs) {
      this.messageSubs.unsubscribe();
    }
    if (this.passwordRecoveredSub) {
      this.passwordRecoveredSub.unsubscribe();
    }
  }
}
